import {
	Component,
	HostListener,
	OnInit,
	Renderer2,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	OnDestroy,
} from '@angular/core';

import {
	NavigationEnd,
	RouteConfigLoadEnd,
	RouteConfigLoadStart,
	Router,
	NavigationStart,
} from '@angular/router';

import { ACTIVITY_TYPES } from 'cargo-chief-commons';

import { RouteTrackingService } from '@service/routeTracking/route-tracking.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { map, Subscription } from 'rxjs';
import { filter, debounceTime, take, takeUntil } from 'rxjs/operators';
import { IntercomService } from '@service/intercom/intercom.service';
import { UtilService } from '@service/util/util.service';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { Store } from '@ngrx/store';
import { SessionActions, sessionStateKey } from '@state/session';
import { BaseComponent } from '@shared/base/base.component';
import { Actions, ofType } from '@ngrx/effects';
import { WebSocketActions } from 'app/v2/services/web-socket/web-socket.actions';
import { PushNotificationService } from '@service/pushNotification/push-notification.service';
import { UserNotificationsService } from '@service/user-notifications/user-notifications.service';
import { FirebaseService } from '@service/firebase/firebase.service';
import { UrlCleanupService } from './services/url-cleanup.service';
import { PwaInstallService } from './services/pwa/pwa-install.service';

declare const FingerprintJS: any;
declare const window: any;
declare const analytics: any;

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
	isLazyLoadingRouterModule: boolean = false;
	isSubscribedToUserNotifications: boolean = false;
	userNotificationsSub?: Subscription;

	constructor(
		private cargoChief: Cargochief,
		private router: Router,
		private routeTrack: RouteTrackingService,
		private intercomService: IntercomService,
		private utilService: UtilService,
		private renderer: Renderer2,
		private store: Store,
		private cdRef: ChangeDetectorRef,
		private readonly ngRxActions: Actions<any>,
		private readonly pushNotificationService: PushNotificationService,
		private readonly userNotificationsService: UserNotificationsService,
		private firebaseService: FirebaseService,
		private urlCleanupService: UrlCleanupService,
		public pwaInstallService: PwaInstallService
	) {
		super();
		this.detectBrowser();

		// Clean up tracking parameters from URL before router processes it
		this.urlCleanupService.initialize();

		if (window.location.href.indexOf('/#!/register') > 0) {
			window.location.href = window.location.href.replace('/#!/', '/#/');
		}
		const vm = this;
		// Load FingerprintJS asynchronously without blocking app startup
		if (this.cargoChief.getDeviceId() == undefined || this.cargoChief.getDeviceId() == null) {
			// Use requestIdleCallback for better performance, fallback to setTimeout
			const loadFingerprint = () => {
				FingerprintJS.load()
					.then((fp) => fp.get())
					.then((result) => localStorage.setItem('deviceId', result.visitorId))
					.catch(() => {
						// Silently handle fingerprinting failures
					});
			};

			if (window.requestIdleCallback) {
				window.requestIdleCallback(loadFingerprint, { timeout: 2000 });
			} else {
				setTimeout(loadFingerprint, 100); // Reduced from 500ms to 100ms
			}
		}
		// Show PWA prompt on login, hide on logout
		this.ngRxActions
			.pipe(takeUntil(this.isDestroyed$), ofType(SessionActions.login.setup.success))
			.subscribe(({ payload }) => {
				this.pwaInstallService.clearDismissalFlag();
				this.pwaInstallService.updatePromptVisibility(payload.user?.id?.toString());
			});

		this.ngRxActions
			.pipe(takeUntil(this.isDestroyed$), ofType(SessionActions.logout.start))
			.subscribe(() => {
				this.pwaInstallService.clearDismissalFlag();
				this.pwaInstallService.updatePromptVisibility(null);
			});

		this.router.events.pipe(takeUntil(this.isDestroyed$)).subscribe((event) => {
			if (event instanceof RouteConfigLoadStart) {
				this.cargoChief.removeItem(CARGOCHIEF_KEYS.NEXT_PAYMENT_DATE_EXPIRED);
				this.isLazyLoadingRouterModule = true;
			} else if (event instanceof RouteConfigLoadEnd) {
				this.isLazyLoadingRouterModule = false;
			}
			if (event instanceof NavigationEnd) {
				try {
					if (!this.cargoChief.USER_ID) {
						this.userNotificationsSub?.unsubscribe();
						this.userNotificationsSub = undefined;
						this.isSubscribedToUserNotifications = false;
					} else {
						this.listenToCarrierUserNotifications();
						this.listenToBrokerUserNotifications();
					}
					const path = event.urlAfterRedirects;
					if (typeof analytics !== 'undefined' && analytics && analytics.page) {
						analytics.page({
							path: path,
							url: window.location.origin + '/#' + path,
						});
					}
				} catch (error) {
					console.warn('Analytics tracking failed:', error);
				}
			}
		});
	}

	@HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
		localStorage.setItem('isForegroundEnabled', 'false');
	}

	detectBrowser() {
		this.router.events
			.pipe(
				take(1),
				takeUntil(this.isDestroyed$),
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe((event: NavigationEnd) => {
				const isIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
				if (isIEOrEdge) {
					this.router.navigate(['change_browser']);
				}
			});
	}

	ngOnInit() {
		this.cargoChief.removeItem(CARGOCHIEF_KEYS.LOGGED_IN);
		this.renderer.listen('window', 'storage', (event) => {
			if (event.key === sessionStateKey) {
				this.store.dispatch(SessionActions.storage.update());
			}
		});
	}

	listenToCarrierUserNotifications() {
		if (
			this.cargoChief.USER_ID &&
			this.utilService.isAccountTypeCarrier() &&
			!this.isSubscribedToUserNotifications
		) {
			if (this.userNotificationsSub) {
				return;
			}
			this.isSubscribedToUserNotifications = true;
			this.userNotificationsSub = this.ngRxActions
				.pipe(
					takeUntil(this.isDestroyed$),
					ofType(WebSocketActions.read),
					filter((message) =>
						['carrier-imports', 'carrier-offer-update'].includes(message?.payload?.subject)
					)
				)
				.subscribe((message) => {
					const payload = message.payload;
					let notificationMessage: Array<any> = null;
					// when a new broker is added to carrier network
					if (payload.subject === 'carrier-imports' && payload?.type === 'ADD') {
						if (this.userNotificationsService.NEW_BROKER_CONNECTED) {
							notificationMessage = [
								{
									title: `${payload?.brokerBusinessName} is now in your network.`,
								},
							];
						}
					} else if (payload.subject === 'carrier-offer-update') {
						if (payload?.bidId && payload?.message) {
							const bidMonitorUrl = `/bid/monitor/${payload.bidId}?carrierOfferUpdate=${encodeURIComponent(payload.message)}`;
							notificationMessage = [
								{
									title: payload.message,
									url: bidMonitorUrl,
								},
							];
						}
					}
					if (notificationMessage) {
						this.pushNotificationService.generateNotification(notificationMessage);
					}
				});
			this.firebaseService.requestPermission();
		}
	}

	listenToBrokerUserNotifications() {
		if (
			this.cargoChief.USER_ID &&
			(this.utilService.isAccountTypeShipper() || this.utilService.isAccountTypeBroker()) &&
			!this.isSubscribedToUserNotifications
		) {
			if (this.userNotificationsSub) {
				return;
			}
			this.isSubscribedToUserNotifications = true;
			this.userNotificationsSub = this.ngRxActions
				.pipe(
					takeUntil(this.isDestroyed$),
					ofType(WebSocketActions.read),
					filter((message) => message?.payload?.subject === 'activities'),
					map((message) => message.payload)
				)
				.subscribe((payload) => {
					// when a carrier user message load owner
					if (payload?.activityType === ACTIVITY_TYPES.LOAD_OWNER_UPDATE) {
						if (this.userNotificationsService.BA_LOAD_OWNER_UPDATE) {
							const notificationMessage: Array<any> = [
								{
									title: `${payload?.message}`,
								},
							];
							this.pushNotificationService.generateNotification(notificationMessage);
						}
					}
				});
			this.firebaseService.requestPermission();
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
